import { useEffect, useMemo, useState } from "react";
import { GridView, Griew, Column, Response, Filter } from "react-griew";
import Pasoonate from "pasoonate";
import BankAccountService from "../../service/bank-account-service";
import i18n from "../../plugins/locale/i18n";
import loadDictionary from "../../plugins/locale/load-dictionary";
import { Button, Col, Form, InputGroup, Row } from "react-bootstrap";

const bankAccountService = new BankAccountService();
const griew = new Griew();
const today = Pasoonate.make().jalali();

await loadDictionary("bank-account-statements-page");

function BankAccountStatementsPage() {
  const [count, setCount] = useState(0);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [filterbankAccountId, setFilterBankAccountId] = useState(0);
  const [filterToDay, setFilterToDay] = useState(today.getDay());
  const [filterToMonth, setFilterToMonth] = useState(today.getMonth());
  const [filterToYear, setFilterToYear] = useState(today.getYear());
  const [filterFromDay, setFilterFromDay] = useState(today.getDay());
  const [filterFromMonth, setFilterFromMonth] = useState(today.getMonth());
  const [filterFromYear, setFilterFromYear] = useState(today.getYear());
  const [isLoading, setIsLoading] = useState(false);
  const monthOptions = useMemo(() => {
        const options = [];
        const monthList = Pasoonate.trans('jalali.month_name', 'fa');
        
        for(let i in monthList) {
            options.push(<option key={i} value={i}>{monthList[i]}</option>);
        }
  
        return options;
    }, []);
  const dayOptions = useMemo(() => {
        const options = [];
        
        for(let i = 1; i <= 31; i++) {
            options.push(<option key={i} value={i}>{i}</option>);
        }
  
        return options;
    }, []);
  const reloadList = () => { 
    setIsLoading(true);

    griew.orders.add('transaction_date', 'DESC');
    griew.options.add('accessible_accounts', true);
    griew.refresh();
  };
  const handleSubmit = () => {
    const from = Pasoonate.make().jalali().setDateTime(filterFromYear, filterFromMonth, filterFromDay, 0, 0, 0).gregorian();
    const to = Pasoonate.make().jalali().setDateTime(filterToYear, filterToMonth, filterToDay, 23, 59, 59).gregorian();

    griew.filters.add('bank_account_id', Column.NUMBER, Filter.IS_EQUAL_TO, filterbankAccountId, null);
    griew.filters.add('transaction_date', Column.DATETIME, Filter.BETWEEN, from.format('yyyy-MM-dd 00:00:00'), to.format('yyyy-MM-dd 23:59:59'))

    griew.pagination.gotoFirst();

    reloadList();
  };

  useEffect(() => {
    prepareGriew(setCount, setIsLoading);
    reloadList();

    bankAccountService.findAllByCompany().then((bankAccounts) => {
      setBankAccounts(bankAccounts);
    });

  }, []);

  return (
      <div className="content-area bg-body-tertiary p-3">
        <div className="row mb-2">
          <div className="table-title col">
            <h5 className="my-1">{i18n.t("bank-account-statements-page.table_title")} ({count})</h5>
          </div>
          <div className="table-toolbar col-auto">
            <button className={"btn btn-sm btn-light mb-2 " + (isLoading ? 'rotate' : '')} type="button" onClick={reloadList}><i className="bi bi-arrow-clockwise"></i></button>
          </div>
        </div>
        <div className="mb-2">
          <Form onSubmit={e => {e.preventDefault(); handleSubmit()}}>
            <Row>
              <Col lg={2} md={4} sm={12}>
                <Form.Select name="bank_account_id" size="sm" className="mb-2" value={filterbankAccountId} onChange={(e) => {setFilterBankAccountId(e.target.value)}}>
                  <option key={0} value={0}>{i18n.t('bank-account-statements-page.filter.bank_account_select')}</option>
                  {bankAccounts.map((account, index) => {
                    return <option key={index} value={account.id}>{account.name}</option>;
                  })}
                </Form.Select>
              </Col>
              <Col lg={2} md={4} sm={6}>
                <InputGroup size="sm" className="mb-2">
                  <Form.Select value={filterFromDay} onChange={(e) => {setFilterFromDay(e.target.value)}}>
                    {dayOptions}
                  </Form.Select>
                  <Form.Select value={filterFromMonth} onChange={(e) => {setFilterFromMonth(e.target.value)}}>
                    {monthOptions}
                  </Form.Select>
                  <Form.Control dir="ltr" value={filterFromYear} onChange={(e) => {setFilterFromYear(e.target.value)}} />
                </InputGroup>
              </Col>
              <Col lg={2} md={4} sm={6}>
                <InputGroup size="sm" className="mb-2">
                  <Form.Select value={filterToDay} onChange={(e) => {setFilterToDay(e.target.value)}}>
                    {dayOptions}
                  </Form.Select>
                  <Form.Select value={filterToMonth} onChange={(e) => {setFilterToMonth(e.target.value)}}>
                    {monthOptions}
                  </Form.Select>
                  <Form.Control dir="ltr" value={filterToYear} onChange={(e) => {setFilterToYear(e.target.value)}} />
                </InputGroup>
              </Col>
              <Col>
                  <Button type="submit" size="sm">{i18n.t('bank-account-statements-page.filter.submit')}</Button>
              </Col>
            </Row>
          </Form>
        </div>
        <div className="table-responsive">
          <GridView griew={griew} ></GridView>
        </div>
      </div>
  );
}

function prepareGriew(setCount, setIsLoading) {
  const list = [
    {
      name: "id",
      caption: i18n.t('bank-account-statements-page.columns.id'),
      schema: "BankAccount",
      type: Column.NUMBER,
      order: 1,
      sortable: false,
      filterable: false,
      visibility: false,
    },
    {
      name: "external_app_name",
      caption: i18n.t('bank-account-statements-page.columns.external_app_name'),
      schema: "BankAccountStatement",
      type: Column.STRING,
      order: 1,
      sortable: false,
      filterable: false,
      visibility: true
    },
    {
      name: "external_app_record_key",
      caption: i18n.t('bank-account-statements-page.columns.external_app_record_key'),
      schema: "BankAccountStatement",
      type: Column.STRING,
      order: 1,
      sortable: false,
      filterable: false,
      visibility: true
    },
    {
      name: "bank_account_id",
      caption: i18n.t('bank-account-statements-page.columns.bank_account_id'),
      schema: "BankAccountStatement",
      type: Column.STRING,
      order: 1,
      sortable: false,
      filterable: false,
      visibility: true,
      beforeRender: (value, record) => {
        return record.source_bank_account_name || value;
      }
    },
    {
      name: "transaction_date",
      caption: i18n.t('bank-account-statements-page.columns.transaction_date'),
      schema: "BankAccountStatement",
      type: Column.STRING,
      order: 1,
      sortable: false,
      filterable: false,
      visibility: true,
      beforeRender: (value) => { 
        const date = Pasoonate.make().gregorian().parse('yyyy-MM-dd HH:mm:ss', value).jalali().format("yy/MM/dd HH:mm");
  
        return <span dir="ltr">{date}</span>;
      }
    },
    {
      name: "type",
      caption: i18n.t('bank-account-statements-page.columns.type'),
      schema: "BankAccountStatement",
      type: Column.STRING,
      order: 1,
      sortable: false,
      filterable: false,
      visibility: true
    },
    {
      name: "debit",
      caption: i18n.t('bank-account-statements-page.columns.debit'),
      schema: "BankAccountStatement",
      type: Column.STRING,
      order: 1,
      sortable: false,
      filterable: false,
      visibility: true,
      beforeRender: (value) => {
        return Number(value).toLocaleString();
      }
    },
    {
      name: "credit",
      caption: i18n.t('bank-account-statements-page.columns.credit'),
      schema: "BankAccountStatement",
      type: Column.STRING,
      order: 1,
      sortable: false,
      filterable: false,
      visibility: true,
      beforeRender: (value) => {
        return Number(value).toLocaleString();
      }
    },
    {
      name: "balance",
      caption: i18n.t('bank-account-statements-page.columns.balance'),
      schema: "BankAccountStatement",
      type: Column.STRING,
      order: 1,
      sortable: false,
      filterable: false,
      visibility: true,
      beforeRender: (value) => {
        return Number(value).toLocaleString();
      }
    },
    {
      name: "currency",
      caption: i18n.t('bank-account-statements-page.columns.currency'),
      schema: "BankAccountStatement",
      type: Column.STRING,
      order: 1,
      sortable: false,
      filterable: false,
      visibility: false
    },
    {
      name: "source_account_number",
      caption: i18n.t('bank-account-statements-page.columns.source_account_number'),
      schema: "BankAccountStatement",
      type: Column.STRING,
      order: 1,
      sortable: false,
      filterable: false,
      visibility: true
    },
    {
      name: "destination_account_number",
      caption: i18n.t('bank-account-statements-page.columns.destination_account_number'),
      schema: "BankAccountStatement",
      type: Column.STRING,
      order: 1,
      sortable: false,
      filterable: false,
      visibility: true
    },
    {
      name: "branch_code",
      caption: i18n.t('bank-account-statements-page.columns.branch_code'),
      schema: "BankAccountStatement",
      type: Column.STRING,
      order: 1,
      sortable: false,
      filterable: false,
      visibility: true
    },
    {
      name: "branch_name",
      caption: i18n.t('bank-account-statements-page.columns.branch_name'),
      schema: "BankAccountStatement",
      type: Column.STRING,
      order: 1,
      sortable: false,
      filterable: false,
      visibility: true
    },
    {
      name: "transaction_key",
      caption: i18n.t('bank-account-statements-page.columns.transaction_key'),
      schema: "BankAccountStatement",
      type: Column.STRING,
      order: 1,
      sortable: false,
      filterable: false,
      visibility: false
    },
    {
      name: "reference_number",
      caption: i18n.t('bank-account-statements-page.columns.reference_number'),
      schema: "BankAccountStatement",
      type: Column.STRING,
      order: 1,
      sortable: false,
      filterable: false,
      visibility: true
    },
    {
      name: "description",
      caption: i18n.t('bank-account-statements-page.columns.description'),
      schema: "BankAccountStatement",
      type: Column.STRING,
      order: 1,
      sortable: false,
      filterable: false,
      visibility: true
    },
    {
      name: "created_at",
      caption: i18n.t('bank-account-statements-page.columns.created_at'),
      schema: "BankAccountStatement",
      type: Column.STRING,
      order: 1,
      sortable: false,
      filterable: false,
      visibility: false,
      beforeRender: (value) => { 
        const date = Pasoonate.make().gregorian().parse('yyyy-MM-dd HH:mm:ss', value).jalali().format("yy/MM/dd HH:mm");
  
        return <span dir="ltr">{date}</span>;
      }
    },
    {
      name: "updated_at",
      caption: i18n.t('bank-account-statements-page.columns.updated_at'),
      schema: "BankAccountStatement",
      type: Column.STRING,
      order: 1,
      sortable: false,
      filterable: false,
      visibility: false ,
      beforeRender: (value) => { 
        const date = Pasoonate.make().gregorian().parse('yyyy-MM-dd HH:mm:ss', value).jalali().format("yy/MM/dd HH:mm");
  
        return <span dir="ltr">{date}</span>;
      }
    }
  ];
  
  const callback = function (promis) {
    promis.then(response => {
      const pagination = response.pagination;
      const data = response.data;

      const res = Response.make({
        data: data,
        pagination: {
          currentPage: pagination?.currentPage ?? 1,
          lastPage: pagination?.lastPage ?? 1,
          perPage: pagination?.perPage ?? 15,
          total: pagination?.total ?? 1,
          from: pagination?.from ?? 1,
          to: pagination?.to ?? 1,
        }
      });

      griew.refresh(res);

      setCount(pagination.total);
      setIsLoading(false);
    })
    .catch(error => {
      console.log(error);
    });
  };
  griew.options.add('table_class_name', 'table-hover'); 
  griew.localization.locale = 'fa';
  griew.columns.fromArray(list);
  griew.providers.default = 'function';
  griew.providers.getProvider().options = {
    function: (query) => bankAccountService.statements(query),
    callback: callback
  };
}

export default BankAccountStatementsPage;